"use client";

import { usePathname } from "next/navigation";
import { useEffect, useReducer, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ReactMarkdown from "react-markdown";
import { useStoreTime } from "~/hooks/hours";
import {
  hasFlag,
  type PriorityMessage,
  usePriorityMessages,
} from "~/hooks/priority-message";
import { Button } from "../Button/Button";
import { Drawer } from "../Drawer/Drawer";
import { MarkdownLink } from "../MarkdownLabel/MarkdownLabel";
import { NavigationDots } from "../NavigationDots/NavigationDots";
import { Stack } from "../Stack/Stack";
import { Text } from "../Text/Text";
import { PinnedMessageBanner } from "./PinnedMessageBanner";
import styles from "./PriorityMessageDrawer.module.scss";

const STORAGE_KEY = "priority-message-dismissed";

type Dismissed = Record<string, string>;

function revisionOf(message: PriorityMessage): string {
  const parsed = Date.parse(message.updatedAt);
  return Number.isNaN(parsed) ? message.updatedAt : String(parsed);
}

function readDismissed(locationRef: string | null): Dismissed {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return {};
    const parsed = JSON.parse(stored);
    if (parsed?.locationRef !== locationRef) return {};
    return typeof parsed.dismissed === "object" && parsed.dismissed !== null
      ? parsed.dismissed
      : {};
  } catch {
    return {};
  }
}

function writeDismissed(locationRef: string | null, dismissed: Dismissed) {
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ locationRef, dismissed }),
    );
  } catch {}
}

// global messages come first, then operator, then office, then anything else
const SOURCE_ORDER = ["operator", "office"];

function displayRank(message: PriorityMessage): number {
  if (message.isGlobal) return 0;
  const rank = SOURCE_ORDER.indexOf(message.source);
  if (rank === -1) return SOURCE_ORDER.length + 1;
  return rank + 1;
}

type Surface = "site" | "cart";

interface DrawerState {
  dismissed: { site: Dismissed; cart: Dismissed };
  stack: PriorityMessage[];
  index: number;
  hydrated: boolean;
}

type DrawerAction =
  | { type: "hydrate"; site: Dismissed }
  | { type: "surface-changed"; surface: Surface }
  | { type: "refill"; messages: PriorityMessage[] }
  | { type: "acknowledge"; surface: Surface; message: PriorityMessage }
  | { type: "open-stack"; messages: PriorityMessage[] }
  | { type: "set-index"; index: number };

function drawerReducer(state: DrawerState, action: DrawerAction): DrawerState {
  switch (action.type) {
    case "hydrate":
      return {
        ...state,
        dismissed: { ...state.dismissed, site: action.site },
        hydrated: true,
      };
    case "surface-changed":
      return {
        ...state,
        dismissed:
          action.surface !== "cart"
            ? { ...state.dismissed, cart: {} }
            : state.dismissed,
        stack: [],
        index: 0,
      };
    case "refill":
      return { ...state, stack: action.messages, index: 0 };
    case "acknowledge": {
      const { surface, message } = action;
      const next = {
        ...state.dismissed[surface],
        [message.messageId]: revisionOf(message),
      };
      return {
        ...state,
        dismissed: { ...state.dismissed, [surface]: next },
        index: state.index + 1,
      };
    }
    case "open-stack":
      return { ...state, stack: action.messages, index: 0 };
    case "set-index":
      return { ...state, index: action.index };
  }
}

type StoreTime = ReturnType<typeof useStoreTime>;

function formatLastUpdated(
  updatedAt: string,
  { formatDate, formatZone }: StoreTime,
): string | null {
  const date = new Date(updatedAt);
  // the formatters throw on an invalid date, taking the whole drawer with it
  if (!Number.isFinite(date.getTime())) return null;

  const time = formatDate(date, "d MMM yyyy 'at' h:mm a");
  const timeZoneName = formatZone(date);
  return timeZoneName ? `${time} ${timeZoneName}` : time;
}

export function PriorityMessageDrawer({
  locationRef,
}: {
  locationRef: string | null;
}): React.ReactNode {
  const { priorityMessages } = usePriorityMessages(locationRef);
  const storeTime = useStoreTime();
  const pathname = usePathname();
  const surface =
    locationRef && pathname?.startsWith(`/${locationRef}/cart`)
      ? "cart"
      : "site";
  const [state, dispatch] = useReducer(drawerReducer, {
    dismissed: { site: {}, cart: {} },
    stack: [],
    index: 0,
    hydrated: false,
  });
  const { dismissed, stack, index, hydrated } = state;
  const [bannerSlot, setBannerSlot] = useState<HTMLElement | null>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [viewportHeight, setViewportHeight] = useState<number>();

  useEffect(() => {
    setBannerSlot(document.getElementById("pinned-message-slot"));
  }, []);

  useEffect(() => {
    dispatch({ type: "hydrate", site: readDismissed(locationRef) });
  }, [locationRef]);

  useEffect(() => {
    dispatch({ type: "surface-changed", surface });
  }, [surface]);

  const visible =
    surface === "cart"
      ? priorityMessages.filter((message) => hasFlag(message, "cart-always"))
      : priorityMessages;

  const candidates = [...visible].sort(
    (a, b) => displayRank(a) - displayRank(b),
  );

  const seen = dismissed[surface];
  const unread = candidates.filter(
    (message) => seen[message.messageId] !== revisionOf(message),
  );

  const closed = index >= stack.length;
  const unreadKey = unread
    .map((message) => `${message.messageId}:${revisionOf(message)}`)
    .join(",");

  // refill only once closed, so a message arriving (or a location changing)
  // mid-read doesn't cut into the current stack
  // biome-ignore lint/correctness/useExhaustiveDependencies: unreadKey stands in for the unread list
  useEffect(() => {
    if (closed && unread.length > 0) {
      dispatch({ type: "refill", messages: unread });
    }
  }, [closed, unreadKey]);

  const activeIndex = Math.min(index, stack.length - 1);
  const active = stack[activeIndex];

  // keep measuring, not just once. The text rewraps when the viewport changes,
  // and a font swap can reflow it after the first paint
  // biome-ignore lint/correctness/useExhaustiveDependencies: a refilled stack swaps the slide behind the same index
  useEffect(() => {
    const slide = slideRefs.current[activeIndex];
    if (!slide) {
      setViewportHeight(undefined);
      return;
    }

    const measure = () => setViewportHeight(slide.offsetHeight);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(slide);
    return () => observer.disconnect();
  }, [activeIndex, stack]);

  const pinned = [...priorityMessages]
    .filter(
      (message) =>
        hasFlag(message, "pinned") &&
        dismissed.site[message.messageId] === revisionOf(message),
    )
    .sort((a, b) => displayRank(a) - displayRank(b));

  const acknowledge = () => {
    if (!active) return;
    dispatch({ type: "acknowledge", surface, message: active });
    if (surface === "site") {
      writeDismissed(locationRef, {
        ...seen,
        [active.messageId]: revisionOf(active),
      });
    }
  };

  const openPinned = () => {
    dispatch({ type: "open-stack", messages: pinned });
  };

  const lastUpdated =
    active && hydrated ? formatLastUpdated(active.updatedAt, storeTime) : null;

  return (
    <>
      {hydrated &&
        bannerSlot &&
        pinned.length > 0 &&
        // portal into the banner slot; jets lacks web-ordering's sticky helpers
        createPortal(
          <PinnedMessageBanner messages={pinned} onOpen={openPinned} />,
          bannerSlot,
        )}
      {stack.length > 0 && (
        <Drawer
          title={active?.header ?? ""}
          open={Boolean(active) && hydrated && !closed}
          onClose={() => {}}
          headerBackground="jets-red"
          headerColor="white"
          zIndex={300}
          priority
        >
          <Stack space="4">
            <div
              className={styles.viewport}
              style={viewportHeight ? { height: viewportHeight } : undefined}
            >
              <div
                className={styles.track}
                style={{ transform: `translateX(-${activeIndex * 100}%)` }}
              >
                {stack.map((message, position) => (
                  <div
                    key={message.messageId}
                    ref={(node) => {
                      slideRefs.current[position] = node;
                    }}
                    className={styles.slide}
                    inert={position !== activeIndex}
                  >
                    <div className={styles.body}>
                      <ReactMarkdown components={{ a: MarkdownLink }}>
                        {message.body}
                      </ReactMarkdown>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {lastUpdated && (
              <Text size="xsmall" color="neutral-500" align="center">
                Last updated {lastUpdated}
              </Text>
            )}
            {stack.length > 1 && (
              <NavigationDots
                count={stack.length}
                activeIndex={activeIndex}
                onDotClick={(next) =>
                  dispatch({ type: "set-index", index: next })
                }
              />
            )}
            <Button primary onClick={acknowledge}>
              Got it
            </Button>
          </Stack>
        </Drawer>
      )}
    </>
  );
}
